// src/Chat/ChatWindow.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Avatar,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  FormHelperText,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import axios from 'axios';

import MessageList from './MessageList';
import MessageInput from './MessageInput';
import CloseIcon from '@mui/icons-material/Close';
import MuiDialog from '../Template/components/UI/MuiDialog/MuiDialog';
import OInput from '../Template/components/UI/FormControls/OInput';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const WA_GREEN = '#ff8f28';
const WA_GREEN_HOVER = '#fd8412ff';
const WA_BG = '#efeae2';
const bgPattern = {
  backgroundImage: 'radial-gradient(#d1d7db 1px, transparent 1px)',
  backgroundSize: '26px 26px',
  backgroundPosition: '0 0',
  backgroundColor: WA_BG,
  maxBlockSize: 'fit-content',
};

const DOKUMENT_ID = 2; // prema response-u: { id: 2, vrednost: "Dokument" }
const TESTIRANJE_ID = 1002; // { id: 1002, vrednost: "Testiranje" }

const ChatWindow = ({ isMobile, onBack, activeChat, setActiveChat }) => {
  const [openPopUp, setOpenPopUp] = useState(false);

  // 1) Kategorije
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [catError, setCatError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(''); // '' | number

  // 2) Oznake dokumenta
  const [docTags, setDocTags] = useState([]);
  const [docTagsLoading, setDocTagsLoading] = useState(false);
  const [docTagsError, setDocTagsError] = useState('');
  const [selectedDocTag, setSelectedDocTag] = useState(''); // '' | string|number

  // Submit
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isEditMsg, setIsEditMsg] = useState(false);
  const [msgForEdit, setMsgForEdit] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);
  const [openGroupMembersDialog, setOpenGroupMembersDialog] = useState(false);
  const [openAddMembersDialog, setOpenAddMembersDialog] = useState(false);
  const [groupMembersList, setGroupMembersList] = useState([]);
  const [usersNotInGroup, setUsersNotInGroup] = useState([]);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState([]);
  const [groupMembersLoading, setGroupMembersLoading] = useState(false);
  const [usersNotInGroupLoading, setUsersNotInGroupLoading] = useState(false);
  const [addMembersLoading, setAddMembersLoading] = useState(false);
  const [addMembersSearch, setAddMembersSearch] = useState('');
  const { t, i18n } = useTranslation();
  const [replyTo, setReplyTo] = useState(null);
  const apiBaseUrl = process.env.REACT_APP_API_CHAT_PROD;

  const getInitials = (name) =>
    (name || '')
      .trim()
      .split(/\s+/)
      .map((n) => n[0])
      .join('')
      .toUpperCase();

  const currentUserId = +localStorage.getItem('idKorisnika');
  const otherUser = activeChat?.users?.find(
    (u) => u.user?.id !== currentUserId,
  );
  const chatName = activeChat?.name || otherUser?.user?.name || 'Nepoznat';
  const isTestiranjeOznaka = (value) =>
    String(value || '').toUpperCase() === 'TESTIRANJE';

  const oznakaFromStorage = localStorage.getItem('oznaka');
  const oznakaFromChat = activeChat?.poslednjaOznaka;
  // TESTIRANJE je samo lokalno (localStorage) u aktivnom chatu — nikad iz poslednjaOznaka
  const safeOznaka =
    oznakaFromStorage &&
    oznakaFromStorage !== 'null' &&
    oznakaFromStorage !== 'undefined'
      ? oznakaFromStorage
      : oznakaFromChat &&
        !isTestiranjeOznaka(oznakaFromChat) &&
        oznakaFromChat !== 'null' &&
        oznakaFromChat !== 'undefined'
      ? oznakaFromChat
      : null;

  // Poništi TESTIRANJE čim se napusti ovaj chat (switch ili unmount)
  useEffect(() => {
    return () => {
      if (
        String(localStorage.getItem('oznaka')).toUpperCase() === 'TESTIRANJE'
      ) {
        localStorage.setItem('oznaka', 'null');
      }
    };
  }, [activeChat?.id]);

  const isDokument = selectedCategory === DOKUMENT_ID;

  const handleConfirm = async () => {
    try {
      setSubmitError('');
      setIsSubmitting(true);

      const isTestiranje = Number(selectedCategory) === TESTIRANJE_ID;

      if (isTestiranje) {
        // Samo lokalna sesija — važi do napuštanja chata; ne šalje se drugim članovima
        localStorage.setItem('oznaka', 'TESTIRANJE');
        setOpenPopUp(false);
        return;
      }

      const payload = {
        idKategorije: String(selectedCategory),
        dokument: String(selectedDocTag),
      };

      await axios.post(
        `${apiBaseUrl}/Conversations/chat_izbor_konverzacije`,
        payload,
      );

      setActiveChat((prev) => ({
        ...prev,
        poslednjaOznaka: String(selectedDocTag) || null,
      }));
      localStorage.setItem(
        'oznaka',
        selectedDocTag ? String(selectedDocTag) : 'null',
      );
      setOpenPopUp(false);
    } catch (err) {
      setSubmitError('Greška pri slanju izbora.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenPopUp = () => {
    // reset stanja pri svakom otvaranju
    setSelectedCategory('');
    setSelectedDocTag('');
    setCatError('');
    setDocTagsError('');
    setSubmitError('');
    setOpenPopUp(true);
  };

  const handleClosePopUp = () => setOpenPopUp(false);

  // Učitavanje kategorija kad se popup otvori
  useEffect(() => {
    if (!openPopUp) return;

    const fetchCategories = async () => {
      setCatLoading(true);
      setCatError('');
      try {
        const { data } = await axios.post(
          `${apiBaseUrl}/Combo/vrati_kategorije_combo_novi`,
          { idKorProm: currentUserId },
        );
        setCategories(Array.isArray(data) ? data : []);
        setSelectedCategory(data[0].id);
      } catch {
        setCatError('Greška pri učitavanju kategorija.');
      } finally {
        setCatLoading(false);
      }
    };

    fetchCategories();
  }, [openPopUp, apiBaseUrl, currentUserId]);

  // Učitavanje oznaka dokumenta – samo ako je izabran "Dokument" i postoji idMasine
  useEffect(() => {
    const idMasine = activeChat?.idMasine;
    if (!openPopUp || !isDokument || !idMasine) {
      setDocTags([]);
      setSelectedDocTag('');
      setDocTagsLoading(false);
      setDocTagsError('');
      return;
    }

    const fetchDocTags = async () => {
      setDocTagsLoading(true);
      setDocTagsError('');
      try {
        const response = await axios.post(
          `${apiBaseUrl}/Conversations/vrati_oznake_dokumenta`,
          { id: idMasine },
        );
        setDocTags(Array.isArray(response.data) ? response.data : []);
      } catch {
        setDocTagsError('Greška pri učitavanju oznaka dokumenta.');
      } finally {
        setDocTagsLoading(false);
      }
    };

    fetchDocTags();
  }, [openPopUp, isDokument, activeChat?.idMasine, apiBaseUrl]);

  const handleDelete = async () => {
    try {
      setSubmitError('');
      setIsSubmitting(true);

      const payload = {
        idKategorije: '1',
        dokument: '',
      };

      await axios.post(
        `${apiBaseUrl}/Conversations/chat_izbor_konverzacije`,
        payload,
      );

      setActiveChat((prev) => ({
        ...prev,
        poslednjaOznaka: null,
      }));
      localStorage.setItem('oznaka', 'null');
      setOpenPopUp(false);
    } catch (err) {
      setSubmitError('Greška pri slanju izbora.');
      setOpenDeleteDialog(false);
      console.error(err);
    } finally {
      setIsSubmitting(false);
      setOpenDeleteDialog(false);
    }
  };
  const handleClearOznaka = () => {
    // TESTIRANJE je samo lokalno — poništi bez API poziva
    if (isTestiranjeOznaka(localStorage.getItem('oznaka'))) {
      localStorage.setItem('oznaka', 'null');
      // forsira re-render badge-a
      setActiveChat((prev) => (prev ? { ...prev } : prev));
      return;
    }
    setOpenDeleteDialog(true);
  };

  const canManageGroupMembers =
    activeChat?.fleg === true ||
    activeChat?.fleg === 1 ||
    String(activeChat?.fleg).toLowerCase() === 'true';

  const closeGroupMenu = () => {
    setGroupMenuAnchor(null);
  };

  const handleOpenGroupMembersDialog = async () => {
    closeGroupMenu();
    if (!activeChat?.id) return;

    try {
      setGroupMembersLoading(true);
      const response = await axios.post(
        `${apiBaseUrl}/Konverzacije/grupa_pregled_korisnika_grupe`,
        { id: activeChat.id },
      );
      setGroupMembersList(Array.isArray(response.data) ? response.data : []);
      setOpenGroupMembersDialog(true);
    } catch (error) {
      toast.error('Greška pri učitavanju članova grupe.');
      console.error('Greška pri učitavanju članova grupe:', error);
    } finally {
      setGroupMembersLoading(false);
    }
  };

  const handleOpenAddMembersDialog = async () => {
    closeGroupMenu();
    if (!activeChat?.id) return;

    try {
      setUsersNotInGroupLoading(true);
      const response = await axios.post(
        `${apiBaseUrl}/Konverzacije/grupa_pregled_korisnika_koji_nisu_u_grupi`,
        { Id: activeChat.id },
      );
      setUsersNotInGroup(Array.isArray(response.data) ? response.data : []);
      setSelectedUsersToAdd([]);
      setAddMembersSearch('');
      setOpenAddMembersDialog(true);
    } catch (error) {
      toast.error('Greška pri učitavanju korisnika van grupe.');
      console.error('Greška pri učitavanju korisnika van grupe:', error);
    } finally {
      setUsersNotInGroupLoading(false);
    }
  };

  const handleAddUsersToGroup = async () => {
    if (!activeChat?.id) return;
    if (!selectedUsersToAdd.length) {
      toast.error('Izaberite korisnike za dodavanje.');
      return;
    }

    try {
      setAddMembersLoading(true);
      const lista = selectedUsersToAdd.map((u) => ({
        id: Number(u.id || u.Id),
      }));
      const response = await axios.post(
        `${apiBaseUrl}/Konverzacije/grupa_dodavanje_novih_korisnika_u_grupu`,
        {
          Id: activeChat.id,
          Lista: lista,
        },
      );
      if (response.status === 200) {
        toast.success('Korisnici su uspešno dodati u grupu.');
        setOpenAddMembersDialog(false);
        setSelectedUsersToAdd([]);
      }
    } catch (error) {
      toast.error('Greška pri dodavanju korisnika u grupu.');
      console.error('Greška pri dodavanju korisnika u grupu:', error);
    } finally {
      setAddMembersLoading(false);
    }
  };

  const mappedUsersNotInGroup = (usersNotInGroup || []).map((u) => ({
    ...u,
    id: Number(u.id || u.Id),
    vrednost: u.name || u.Name || '',
  }));
  const filteredUsersNotInGroup = mappedUsersNotInGroup.filter((u) =>
    u.vrednost.toLowerCase().includes(addMembersSearch.toLowerCase()),
  );
  const selectedUsersPreview = selectedUsersToAdd
    .map((user) => user.vrednost)
    .slice(0, 2)
    .join(', ');

  const toggleUserToAdd = (user) => {
    const exists = selectedUsersToAdd.some(
      (item) => Number(item.id) === Number(user.id),
    );
    if (exists) {
      setSelectedUsersToAdd((prev) =>
        prev.filter((item) => Number(item.id) !== Number(user.id)),
      );
      return;
    }
    setSelectedUsersToAdd((prev) => [...prev, user]);
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0, // ⬅️ ključno za skrol unutar flex kolone
        bgcolor: '#eaf1fb',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0, // ⬅️ header fiksne visine
          p: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #ccc',
          bgcolor: '#fff',
        }}
      >
        <Box display="flex" alignItems="center">
          {isMobile && (
            <IconButton onClick={onBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          {activeChat?.logo ? (
            <Avatar src={activeChat.logo} alt={chatName} sx={{ mr: 1 }} />
          ) : (
            <Avatar sx={{ mr: 1 }}>{getInitials(chatName)}</Avatar>
          )}
          <Box>
            <Typography fontWeight={600}>{chatName}</Typography>
            {safeOznaka && (
              <Box
                component="span"
                sx={{
                  mt: 0.5,
                  display: 'inline-block',
                  bgcolor: 'orange',
                  color: 'white',
                  px: 1,
                  py: 0.25,
                  borderRadius: '4px',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  animation: 'pulse 1.2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                    '100%': { opacity: 1 },
                  },
                }}
              >
                {safeOznaka}
                <IconButton
                  size="small"
                  onClick={() => handleClearOznaka(activeChat.id)}
                  sx={{
                    color: 'white',
                    ml: 0.5,
                    p: 0, // bez paddinga da X bude mali
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton sx={{ ml: 1 }} onClick={handleOpenPopUp}>
            <AddIcon sx={{ color: WA_GREEN }} />
          </IconButton>
          {canManageGroupMembers && (
            <>
              <IconButton onClick={(e) => setGroupMenuAnchor(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={groupMenuAnchor}
                open={Boolean(groupMenuAnchor)}
                onClose={closeGroupMenu}
              >
                <MenuItem onClick={handleOpenGroupMembersDialog}>
                  Pregled članova
                </MenuItem>
                <MenuItem onClick={handleOpenAddMembersDialog}>
                  Dodavanje članova
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* LISTA PORUKA – izdvojen wrapper sa minHeight: 0 */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', ...bgPattern }}>
        <MessageList
          isEditMsg={isEditMsg}
          setIsEditMsg={setIsEditMsg}
          msgForEdit={msgForEdit}
          setMsgForEdit={setMsgForEdit}
          setActiveChat={setActiveChat}
          replyTo={replyTo}
          setReplyTo={setReplyTo}
        />
      </Box>

      {/* Input */}
      <Box
        sx={{
          flexShrink: 0, // ⬅️ footer fiksne visine
          // px: 0.5,
          // py: 0.5,
          borderTop: '1px solid #ccc',
          bgcolor: '#dbe7fb',
        }}
      >
        <MessageInput
          activeChat={activeChat}
          isEditMsg={isEditMsg}
          setIsEditMsg={setIsEditMsg}
          msgForEdit={msgForEdit}
          setMsgForEdit={setMsgForEdit}
          replyTo={replyTo}
          setReplyTo={setReplyTo}
        />
      </Box>

      {/* Popup */}
      <Dialog
        open={openPopUp}
        onClose={handleClosePopUp}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            {t('pickCategory')}
          </Typography>

          {/* Kategorija */}
          <FormControl
            fullWidth
            variant="outlined"
            margin="normal"
            disabled={catLoading}
            error={!!catError}
          >
            <InputLabel id="kategorija-label" shrink>
              {t('categoryChat')}
            </InputLabel>
            <Select
              labelId="kategorija-label"
              id="kategorija-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Kategorija"
              displayEmpty
              sx={{ height: 56, '& .MuiSelect-select': { py: 1.5 } }}
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.vrednost}
                </MenuItem>
              ))}
            </Select>
            {!!catError && <FormHelperText error>{catError}</FormHelperText>}
          </FormControl>

          {catLoading && (
            <Box mt={1} display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2">{t('loadCategory')}</Typography>
            </Box>
          )}

          {/* Oznake dokumenta – samo za "Dokument" */}
          {isDokument && (
            <>
              <FormControl
                fullWidth
                variant="outlined"
                margin="normal"
                disabled={
                  docTagsLoading || !!docTagsError || docTags.length === 0
                }
              >
                <InputLabel id="oznaka-doc-label" shrink>
                  {t('markCategory')}
                </InputLabel>
                <Select
                  labelId="oznaka-doc-label"
                  id="oznaka-doc-select"
                  value={selectedDocTag}
                  onChange={(e) => setSelectedDocTag(e.target.value)}
                  label={t('markCategory')}
                  displayEmpty
                  sx={{ height: 56, '& .MuiSelect-select': { py: 1.5 } }}
                >
                  {docTags.map((t) => (
                    <MenuItem key={t.id} value={t.vrednost}>
                      {t.vrednost}
                    </MenuItem>
                  ))}
                </Select>

                {!!docTagsError && (
                  <FormHelperText error>{docTagsError}</FormHelperText>
                )}
                {!docTagsError && docTags.length === 0 && !docTagsLoading && (
                  <FormHelperText>{t('noAvailableMark')}</FormHelperText>
                )}
              </FormControl>

              {docTagsLoading && (
                <Box mt={1} display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">{t('loadingMark')}</Typography>
                </Box>
              )}
            </>
          )}

          {/* Submit greška */}
          {submitError && (
            <Typography variant="body2" color="error" mt={1}>
              {submitError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            sx={{
              bgcolor: WA_GREEN,
              '&:hover': { bgcolor: WA_GREEN_HOVER },
              fontWeight: 600,
            }}
            onClick={handleConfirm}
            disabled={
              isSubmitting ||
              !selectedCategory ||
              (isDokument && !selectedDocTag)
            }
            startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
          >
            {isSubmitting ? 'Slanje…' : t('add')}
          </Button>
        </DialogActions>
      </Dialog>
      <MuiDialog
        maxWidth="sm"
        open={openDeleteDialog}
        text="Da li ste sigurni da želite da uklonite oznaku?"
        handleClose={() => setOpenDeleteDialog(false)}
        handleConfirm={handleDelete}
        deniedText="Zatvori"
      />
      <Dialog
        open={openGroupMembersDialog}
        onClose={() => setOpenGroupMembersDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            Članovi grupe
          </Typography>
          {groupMembersLoading ? (
            <Box mt={1} display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2">Učitavanje članova…</Typography>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {groupMembersList.map((member, idx) => {
                const memberName = member.name || member.Name || 'Nepoznato';
                const memberEmail = member.email || member.Email || '';
                return (
                  <ListItem key={member.id || member.Id || idx} sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar>{getInitials(memberName)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={memberName}
                      secondary={memberEmail}
                    />
                  </ListItem>
                );
              })}
              {!groupMembersList.length && (
                <Typography variant="body2" color="text.secondary">
                  Nema članova za prikaz.
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenGroupMembersDialog(false)}
            variant="outlined"
            sx={{
              color: WA_GREEN,
              borderColor: WA_GREEN,
              bgcolor: '#fff',
              '&:hover': {
                bgcolor: 'rgba(255, 143, 40, 0.08)',
                borderColor: WA_GREEN,
              },
            }}
          >
            Zatvori
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openAddMembersDialog}
        onClose={() => setOpenAddMembersDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            Dodavanje članova u grupu
          </Typography>
          {usersNotInGroupLoading ? (
            <Box mt={1} display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2">Učitavanje korisnika…</Typography>
            </Box>
          ) : (
            <>
              <OInput
                label="Pretraga korisnika"
                name="addMembersSearch"
                value={addMembersSearch}
                handleChange={(event) =>
                  setAddMembersSearch(event.target.value)
                }
                style={{ width: '100%' }}
              />

              <List sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5 }}>
                {filteredUsersNotInGroup.map((user) => (
                  <ListItemButton
                    key={user.id}
                    onClick={() => toggleUserToAdd(user)}
                    sx={{ borderRadius: 1 }}
                  >
                    <Checkbox
                      checked={selectedUsersToAdd.some(
                        (item) => Number(item.id) === Number(user.id),
                      )}
                    />
                    <ListItemText primary={user.vrednost} />
                  </ListItemButton>
                ))}
                {!filteredUsersNotInGroup.length && (
                  <Typography sx={{ p: 1, color: 'text.secondary' }}>
                    Nema rezultata.
                  </Typography>
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenAddMembersDialog(false)}
            variant="outlined"
            sx={{
              color: WA_GREEN,
              borderColor: WA_GREEN,
              bgcolor: '#fff',
              '&:hover': {
                bgcolor: 'rgba(255, 143, 40, 0.08)',
                borderColor: WA_GREEN,
              },
            }}
          >
            Otkaži
          </Button>
          <Button
            variant="contained"
            onClick={handleAddUsersToGroup}
            disabled={addMembersLoading || usersNotInGroupLoading}
            sx={{
              bgcolor: WA_GREEN,
              '&:hover': { bgcolor: WA_GREEN_HOVER },
            }}
          >
            {addMembersLoading ? 'Dodavanje…' : 'Dodaj'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatWindow;
